// The use case page's formula chain: "How the number is built" as rows of cards that are also the
// editing surface. White cards edit (an always-on borderless input is the big number), blue cards are
// computed, tangerine cards come from deal economics (a read-only link to the deal step), the final lagging card is lime, constants are grey chips
// under the card they feed.
// Every chain is a grid of N columns, N = the card count of its widest row (at most 4); every card in
// the chain is the same width (a quarter of the content width at N=4, a third at N=3, and a block of
// two thirds / one third, centred, at N=2 / N=1). Shorter rows are right-aligned, so a row's last card
// is always the rightmost column and the totals stack. An orthogonal rounded connector (SVG overlay,
// measured from the DOM) joins the end of one row to the leading operator of the next row's first
// card, wherever that card sits.
//
// Pure presentation over `buildChain` rows (use-case-page-model.ts): no store, no engine. Editing
// goes through the `AnswerEditor` / `FactorEditor` the page hands in; with no editor
// the cards render their values disabled.

import { useEffect, useState } from 'react';
import { AUTOFILL_IGNORE_PROPS } from '../../../_shared/autofill';
import { parseNumberInput } from '../../flow/number-input';
import { NumberField } from './NumberField';
import { chainColumns, rowColumns, type ChainCardVm, type ChainItemVm, type ChainRowVm, type QuestionVm } from './use-case-page-model';

export interface AnswerEditor {
  texts: Record<string, string>;
  onChange: (q: QuestionVm, text: string) => void;
  onReset: (q: QuestionVm) => void;
}

export interface FactorEditor {
  text: string;
  pct: number;
  isDefault: boolean;
  onChange: (text: string) => void;
  onReset: () => void;
}

const OPERATOR_LABEL: Record<string, string> = { '×': 'times', '−': 'minus', '+': 'plus', '=': 'equals' };

const PREFIX: Record<string, string> = { money: '$', rate: '$' };
const SUFFIX: Record<string, string> = { pct: '%', hrs: ' hrs', min: ' min' };

const bare = (defText: string) => defText.replace(/^\$|%$| hrs$| min$/g, '');

function Status({ assumed, onReset }: { assumed: boolean; onReset: () => void }) {
  return assumed ? (
    <span className="vc3-assumed vc3-cc-status">Assumed</span>
  ) : (
    <button type="button" className="vc3-linkbtn vc3-cc-status" onClick={onReset}>
      Reset
    </button>
  );
}

interface FieldProps {
  id: string;
  text: string;
  placeholder: string;
  kind: string | null;
  disabled: boolean;
  big?: boolean;
  label: string;
  onChange: (text: string) => void;
}

function CardInput({ id, text, placeholder, kind, disabled, big, label, onChange }: FieldProps) {
  const invalid = text.trim() !== '' && parseNumberInput(text) === null;
  return (
    <span className={big ? 'vc3-cc-field vc3-cc-field-big' : 'vc3-cc-field'}>
      {kind && PREFIX[kind] && <span aria-hidden>{PREFIX[kind]}</span>}
      <NumberField
        id={id}
        className="vc3-cc-input"
        {...AUTOFILL_IGNORE_PROPS}
        aria-label={label}
        aria-invalid={invalid}
        disabled={disabled}
        value={disabled ? placeholder : text}
        placeholder={placeholder}
        onChange={onChange}
      />
      {kind && SUFFIX[kind] && <span aria-hidden>{SUFFIX[kind]}</span>}
    </span>
  );
}

interface CardCtx {
  ucId: string;
  questions: Record<string, QuestionVm>;
  answers: AnswerEditor | null;
  realization: FactorEditor | null;
  attribution: FactorEditor | null;
  /** Where a deal card's Edit link goes. */
  onEditDeal: (() => void) | null;
}

function Card({ c, ctx }: { c: ChainCardVm; ctx: CardCtx }) {
  const base = `vc3-cc-${ctx.ucId}-${c.id}`;
  const desc = <div className="vc3-cc-desc" data-factor={c.type === 'factor' ? true : undefined}>{c.description}</div>;
  const label = (
    <div className="vc3-cc-label">
      {c.label}
      {c.caption && <span className="vc3-cc-chip">{c.caption}</span>}
    </div>
  );
  const readOnly = !ctx.answers;

  if (c.type === 'input' || c.type === 'delta') {
    const qs = c.keys.map((k) => ctx.questions[k]).filter((q): q is QuestionVm => !!q);
    const assumed = qs.every((q) => q.assumed);
    const editor = ctx.answers;
    const field = (q: QuestionVm, big: boolean, suffixLabel?: string) => (
      <CardInput
        key={q.key}
        id={`${base}-${q.key}`}
        text={editor?.texts[q.key] ?? ''}
        placeholder={editor ? bare(q.defText) : q.def.toLocaleString('en-US')}
        kind={q.kind}
        disabled={readOnly}
        big={big}
        label={suffixLabel ? `${c.label}: ${suffixLabel}` : c.label}
        onChange={(t) => editor?.onChange(q, t)}
      />
    );
    return (
      <div className="vc3-cc" data-type={c.type} data-assumed={!!editor && assumed}>
        <div className="vc3-cc-head">
          {label}
          {editor && qs.length > 0 && <Status assumed={assumed} onReset={() => qs.forEach((q) => !q.assumed && editor.onReset(q))} />}
        </div>
        {c.type === 'input' && qs[0] ? (
          <div className="vc3-cc-val">{field(qs[0], true)}</div>
        ) : (
          <>
            <div className="vc3-cc-val" aria-live="polite">{c.display}</div>
            {c.winRateLift && qs.length === 1 && (
              <div className="vc3-cc-inline">
                {field(qs[0], false, 'relative lift')}
                <span />
                <span />
                <span className="vc3-cc-inline-t">relative lift</span>
              </div>
            )}
            {c.compound && qs.length === 4 && (
              <div className="vc3-cc-parts">
                <span className="vc3-cc-part">
                  <span className="vc3-cc-part-t">Match today → with Clay</span>
                  {field(qs[0], false, 'match rate today')}
                  <span aria-hidden>→</span>
                  {field(qs[1], false, 'match rate with Clay')}
                </span>
                <span className="vc3-cc-part">
                  <span className="vc3-cc-part-t">Conversion today</span>
                  {field(qs[2], false, 'conversion today')}
                </span>
                {/* the lift acts on today's conversion (with Clay = today × (1 + lift)), so an arrow, not a divider */}
                <span className="vc3-cc-part" data-join="arrow">
                  <span aria-hidden>→</span>
                  <span className="vc3-cc-part-t">Speed-to-lead lift</span>
                  {field(qs[3], false, 'speed-to-lead relative lift')}
                </span>
              </div>
            )}
            {qs.length === 2 && (
              <div className="vc3-cc-inline">
                {field(qs[0], false, 'today')}
                <span aria-hidden>→</span>
                {field(qs[1], false, 'with Clay')}
                <span className="vc3-cc-inline-t">today</span>
                <span />
                <span className="vc3-cc-inline-t">with Clay</span>
              </div>
            )}
          </>
        )}
        {desc}
      </div>
    );
  }

  if (c.type === 'factor' && c.factor) {
    const f = c.factor === 'realization' ? ctx.realization : ctx.attribution;
    return (
      <div className="vc3-cc" data-type="factor" data-assumed={f?.isDefault ?? false}>
        <div className="vc3-cc-head">
          {label}
          {f && <Status assumed={f.isDefault} onReset={f.onReset} />}
        </div>
        <div className="vc3-cc-val">
          {f ? (
            <CardInput
              id={`${base}-pct`}
              text={f.isDefault ? '' : f.text}
              placeholder={f.text}
              kind="pct"
              disabled={false}
              big
              label={c.label}
              onChange={f.onChange}
            />
          ) : (
            c.display
          )}
        </div>
        {desc}
      </div>
    );
  }

  if (c.type === 'deal' && c.dealField) {
    return (
      <div className="vc3-cc" data-type="deal" data-src="deal">
        <div className="vc3-cc-head">{label}</div>
        <div className="vc3-cc-val">{c.display}</div>
        <div className="vc3-cc-from">
          From deal economics
          {ctx.onEditDeal && (
            <>
              {' · '}
              <button type="button" className="vc3-linkbtn" onClick={ctx.onEditDeal}>
                Edit
              </button>
            </>
          )}
        </div>
        {desc}
      </div>
    );
  }

  // Program value (the realization output) and Clay-attributed value get their own hue and double
  // emphasis everywhere; the model marks neither, so the tone is read from the card here.
  const tone = c.id.endsWith('rz-out') ? 'program' : undefined;
  return (
    <div
      className="vc3-cc"
      data-type={c.type}
      data-tone={tone}
      data-strong={c.strong || tone || c.type === 'attributed' ? true : undefined}
    >
      <div className="vc3-cc-head">{label}</div>
      <div className="vc3-cc-val">{c.display}</div>
      {desc}
    </div>
  );
}

/** Computed cards: a row's last one sits in the right-hand totals column. */
const COMPUTED = new Set<ChainCardVm['type']>(['subtotal', 'result', 'attributed']);

function Item({ it, ctx, endMark, col }: { it: ChainItemVm; ctx: CardCtx; endMark: string | null; col: number }) {
  return (
    <li
      className="vc3-cc-item"
      style={{ '--vc3-gc': (col - 1) * 2 + 1, '--vc3-gs': it.span * 2 } as React.CSSProperties}
      data-snake-from={endMark ?? undefined}
      data-total={endMark && COMPUTED.has(it.type) ? true : undefined}
    >
      {it.operator ? (
        <span className="vc3-op" role="img" aria-label={OPERATOR_LABEL[it.operator]} data-lead={it.operator}>
          {it.operator}
        </span>
      ) : (
        // Keeps the card exactly one column wide when it has no leading operator.
        <span className="vc3-op" aria-hidden />
      )}
      <Card c={it} ctx={ctx} />
    </li>
  );
}

/* ------------------------------------------------------------------------ connectors */

const CORNER = 10;
const CHEVRON = 6;
/** Straight run between the end of the final curve and the chevron's rear, so the head never sits on the bend. */
const ENTRY_RUN = 12;

/**
 * An orthogonal "Figma connector" with rounded corners from point A (right edge of a row's last
 * card) to B (the tip, just before the next row's leading operator): out to the right, down to the
 * gap between the rows, left to just before the next row, down, then right into B.
 */
export function connectorPath(ax: number, ay: number, bx: number, by: number, outX: number, gapY: number, backX: number): string {
  const r = CORNER;
  return [
    `M ${ax} ${ay}`,
    `H ${outX - r}`,
    `Q ${outX} ${ay} ${outX} ${ay + r}`,
    `V ${gapY - r}`,
    `Q ${outX} ${gapY} ${outX - r} ${gapY}`,
    `H ${backX + r}`,
    `Q ${backX} ${gapY} ${backX} ${gapY + r}`,
    `V ${by - r}`,
    `Q ${backX} ${by} ${backX + r} ${by}`,
    `H ${bx}`,
  ].join(' ');
}

export interface Connector {
  d: string;
  chevron: string;
}

export interface SnakeInput {
  /** Right edge and vertical centre of the row's last card. */
  ax: number;
  ay: number;
  /** Left edge of the next row's first item (its operator gutter) and the glyph's left edge. */
  itemLeft: number;
  opLeft: number;
  /** Vertical centre of the next row's operator, and the empty band between the two rows. */
  by: number;
  gapY: number;
}

/**
 * Connector geometry: out of the last card, down into the band between rows, back to the left edge
 * of the next row's first item (clear of its operator gutter, whatever column that is), down, and
 * right into the operator glyph: a full-radius corner, a straight run, then the chevron.
 */
export function snakeGeometry(i: SnakeInput) {
  const outX = i.ax + 16;
  const bx = i.opLeft - 2;
  // The vertical drops far enough left that the bottom corner keeps its full radius (same as the top
  // one) and a straight run of ENTRY_RUN precedes the chevron. That can be left of the item's own
  // edge (the gutter is narrower than corner + run + chevron): the SVG overflows into the empty cell
  // or the chain's left margin, never over a card.
  const backX = Math.min(bx - CHEVRON - ENTRY_RUN - CORNER, i.ax - 2 * CORNER);
  return { outX, backX, bx, ok: i.gapY - i.ay >= 2 * CORNER && i.by - i.gapY >= 2 * CORNER && bx - backX >= CORNER + ENTRY_RUN + CHEVRON };
}

/**
 * One snake connector, relative to `box`: out of the right edge of `from`, down to the band at
 * `bandY` (viewport y), back left past `itemLeft`, and right into the operator glyph inside `lead`.
 * Null when there is not room for the corners. Shared by the use case chains and the ROI summary.
 */
export function snakeConnector(box: DOMRect, from: DOMRect, lead: HTMLElement, itemLeft: number, bandY: number): Connector | null {
  // The glyph itself, not its gutter box (the glyph is centred in the gutter).
  const range = document.createRange();
  range.selectNodeContents(lead);
  const glyph = range.getBoundingClientRect();
  const t = glyph.width > 0 ? glyph : lead.getBoundingClientRect();
  const ax = from.right - box.left;
  const ay = from.top + from.height / 2 - box.top;
  const by = t.top + t.height / 2 - box.top;
  const gapY = bandY - box.top;
  const g = snakeGeometry({ ax, ay, itemLeft: itemLeft - box.left, opLeft: t.left - box.left, by, gapY });
  if (!g.ok) return null;
  return {
    d: connectorPath(ax, ay, g.bx, by, g.outX, gapY, g.backX),
    chevron: `M ${g.bx - CHEVRON} ${by - CHEVRON} L ${g.bx} ${by} L ${g.bx - CHEVRON} ${by + CHEVRON}`,
  };
}

/** The connector SVG layer (absolutely positioned over its `position: relative` container). */
export function ConnectorPaths({ paths }: { paths: Connector[] }) {
  return (
    <svg className="vc3-connectors" aria-hidden focusable="false">
      {paths.map((p) => (
        <g key={p.d} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d={p.d} />
          <path d={p.chevron} />
        </g>
      ))}
    </svg>
  );
}

/** Measure every row-to-row connector from DOM positions, relative to `el`. */
function measureConnectors(el: HTMLElement): Connector[] {
  const box = el.getBoundingClientRect();
  const rows = [...el.querySelectorAll<HTMLElement>('[data-row]')];
  const out: Connector[] = [];
  for (let i = 0; i + 1 < rows.length; i++) {
    const nextLead = rows[i + 1].querySelector<HTMLElement>('[data-lead]');
    const nextFirst = rows[i + 1].querySelector<HTMLElement>(':scope > .vc3-cc-item');
    const lastCard = rows[i].querySelector<HTMLElement>('[data-snake-from="end"] .vc3-cc');
    if (!nextLead || !nextFirst || nextFirst.firstElementChild !== nextLead || !lastCard) continue;
    if (rows[i + 1].getAttribute('data-connect') === 'false') continue;
    const f = lastCard.getBoundingClientRect();
    const rowBottom = rows[i].getBoundingClientRect().bottom;
    const nextTop = rows[i + 1].getBoundingClientRect().top;
    const c = snakeConnector(box, f, nextLead, nextFirst.getBoundingClientRect().left, (rowBottom + nextTop) / 2);
    if (c) out.push(c);
  }
  return out;
}

/** Re-measures when the container, a row or a card changes size, so connectors stay attached. */
function Connectors({ container, signature }: { container: HTMLDivElement | null; signature: string }) {
  const [paths, setPaths] = useState<Connector[]>([]);
  useEffect(() => {
    const el = container;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const next = measureConnectors(el);
      setPaths((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    el.querySelectorAll('[data-row], .vc3-cc-item').forEach((n) => ro.observe(n));
    return () => ro.disconnect();
  }, [container, signature]);
  return <ConnectorPaths paths={paths} />;
}

/* ---------------------------------------------------------------------------- chain */

export function FormulaChain({ rows, ...ctx }: { rows: ChainRowVm[] } & CardCtx) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const signature = rows.map((r) => `${r.id}:${r.items.length}`).join('|');
  const columns = chainColumns(rows);
  return (
    <div
      className="vc3-chain vc3-chain-cards"
      ref={setContainer}
      data-cols={columns}
      style={{ '--vc3-n': columns } as React.CSSProperties}
    >
      <Connectors container={container} signature={signature} />
      {rows.map((row) => {
        const lastIdx = row.items.length - 1;
        const cols = rowColumns(row, columns);
        return (
          <div key={row.id} className="vc3-chain-block">
            {row.heading && <h3 className="vc3-ucp-h3 vc3-chain-heading">{row.heading}</h3>}
            <ol className="vc3-chain-row" data-row={row.id} data-connect={!row.heading}>
              {row.items.map((it, j) => (
                <Item key={it.id} it={it} ctx={ctx} endMark={j === lastIdx ? 'end' : null} col={cols[j] ?? 1} />
              ))}
            </ol>
          </div>
        );
      })}
    </div>
  );
}
